import {
  Flex,
  Box,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';
import { isNull, isEmpty, find, isNil } from 'lodash-es';
import PropTypes from 'prop-types';
import useDollarsTextFromCents from '../../../../../../../hooks/useDollarsTextFromCents';
import { translateModel } from '../../../../../../../utils/translate';
import { useAppContext } from '../../../../../context/appContext';
import { useProfileContext } from '../../../context/profileContext';
import { useTranslation } from 'react-i18next';
import {
  BirthdayCake as BirthdayCakeIcon,
  CheckBoard as CheckBoardIcon,
  Clock as ClockIcon,
  CrownCircle as CrownCircleIcon,
  MemberCard as MemberCardIcon,
} from '../../../../../../context/Chakra/Icons';
import { MEMBER_TIER_EFFECT_TYPE_WITHIN_INTERVAL } from '../../../constants/memberTiers';
import {
  memberTierRuleType,
  merchantMembershipTierType,
} from '../../../propTypes';
import TierDetailRow from '../components/TierDetailRow';
import { formatDate, transformPercentageToDiscount } from '../utils';

const MemberDetailList = ({
  merchantTiers = [],
  extendBalanceStatusWording = '',
  extendRule,
  upgradeRule,
}) => {
  const { slFeatureService, merchantData, userSetting } = useAppContext();
  const { userData } = useProfileContext();
  const { t, i18n } = useTranslation();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const getDollarsTextFromCents = useDollarsTextFromCents();

  const membershipTier = userData.membership_tier;
  const membershipTierGap = userData.membership_tier_gap;
  const isUpgradeReminderActive =
    userSetting?.upgrade_reminder?.status === 'active';
  const isTopLevel =
    isNull(membershipTierGap.next_tier) && !isEmpty(membershipTier);
  const hasExtendReminder = slFeatureService.hasFeature('extend_reminder');
  const isTierUnlimit =
    !userData.tier_expires_at ||
    membershipTier.valid_period.type === 'unlimited';

  const getTierCondition = useCallback(
    (rule) => {
      if (isNil(rule)) {
        return null;
      }
      const spending = getDollarsTextFromCents(rule.total_spending.cents);
      const isWithinInterval =
        rule.condition_interval.type ===
        MEMBER_TIER_EFFECT_TYPE_WITHIN_INTERVAL;
      if (rule.effect_type === 'extend') {
        return t('users.membership.extend_valid_period', {
          months: rule.effect_interval.time_value,
          type: isWithinInterval
            ? t('users.membership.within_interval')
            : t('users.membership.single_purchase'),
          spending: spending,
        });
      }
      if (isWithinInterval) {
        return t('users.membership.spending_within_months', {
          months: rule.condition_interval.time_value,
          spending: spending,
        });
      }
      return t('users.membership.spending_single_purchase', {
        spending: spending,
      });
    },
    [getDollarsTextFromCents, t],
  );

  const tierConditions = useMemo(() => {
    return merchantTiers.map(({ membership_tier_rules: rules }) => {
      const upgradeRule = find(rules, function (rule) {
        return rule.effect_type === 'upgrade';
      });
      const extendRule = find(rules, function (rule) {
        return rule.effect_type === 'extend';
      });
      return {
        upgradeCondition: getTierCondition(upgradeRule),
        tierExpireDate:
          upgradeRule?.effect_interval.type ===
          MEMBER_TIER_EFFECT_TYPE_WITHIN_INTERVAL
            ? t('users.membership.tier.expire.month', {
                month: upgradeRule.effect_interval.time_value,
              })
            : t('users.membership.expire.unlimited'),
        extendCondition: getTierCondition(extendRule),
      };
    });
  }, [getTierCondition, merchantTiers, t]);

  const extendTierCondition = getTierCondition(extendRule);
  const isShowOldExtendConditionWording =
    !hasExtendReminder && isUpgradeReminderActive && extendTierCondition;
  const isShowNewExtendStatusWording =
    isUpgradeReminderActive &&
    (!extendBalanceStatusWording || isTopLevel) &&
    !isTierUnlimit &&
    extendTierCondition;

  return (
    <Box
      className="membership-detail-list"
      padding="15px 20px 5px 20px"
      backgroundColor="#F8F8F8"
    >
      {membershipTier && (
        <TierDetailRow
          icon={
            <ClockIcon
              className="member-center-profile__member-reminder-icon"
              fontSize="20px"
            />
          }
          label={t('users.membership.expiry_date')}
          description={
            userData.tier_expires_at
              ? formatDate(userData.tier_expires_at)
              : t('users.membership.expire.unlimited')
          }
        />
      )}
      {membershipTier?.member_discount_percentage && (
        <TierDetailRow
          icon={
            <BirthdayCakeIcon
              className="member-center-profile__member-reminder-icon"
              fontSize="20px"
            />
          }
          label={t('users.membership.offer.discount')}
          description={t('users.membership.tier.discount_percentage.caption', {
            value: transformPercentageToDiscount(
              membershipTier.member_discount_percentage,
              i18n.language,
            ),
          })}
        />
      )}
      {isUpgradeReminderActive && !!membershipTierGap.next_tier && (
        <TierDetailRow
          icon={
            <MemberCardIcon
              className="member-center-profile__member-reminder-icon"
              fontSize="20px"
            />
          }
          label={
            <span>
              {t('users.membership.upgrade.next_tier', {
                tier_name: translateModel(
                  membershipTierGap.next_tier.name_translations,
                  i18n.language,
                ),
              })}
              {membershipTierGap.next_discount && (
                <span>
                  {t('users.membership.upgrade.next_tier.discount', {
                    discount: transformPercentageToDiscount(
                      membershipTierGap.next_discount,
                      i18n.language,
                    ),
                  })}
                </span>
              )}
            </span>
          }
          description={getTierCondition(upgradeRule)}
        />
      )}
      {isUpgradeReminderActive && !membershipTierGap.next_tier && (
        <TierDetailRow
          icon={
            <MemberCardIcon
              className="member-center-profile__member-reminder-icon"
              fontSize="20px"
            />
          }
          label={t('users.membership.max_level_tier')}
        />
      )}
      {(isShowOldExtendConditionWording || isShowNewExtendStatusWording) && (
        <TierDetailRow
          icon={
            isShowOldExtendConditionWording ? (
              <CheckBoardIcon
                className="member-center-profile__member-reminder-icon"
                fontSize="20px"
              />
            ) : (
              <CrownCircleIcon
                className="member-center-profile__member-reminder-icon"
                fontSize="20px"
              />
            )
          }
          label={t('users.membership.extension.condition')}
          description={extendTierCondition}
        />
      )}
      {isUpgradeReminderActive &&
        !isEmpty(extendBalanceStatusWording) &&
        !isTopLevel && (
          <TierDetailRow
            icon={
              <CrownCircleIcon
                className="member-center-profile__member-reminder-icon"
                fontSize="20px"
              />
            }
            label={t('users.membership.extension.status')}
            description={extendBalanceStatusWording}
          />
        )}
      {isUpgradeReminderActive && (
        <>
          <Box textAlign="left">
            <Button
              variant="textButton"
              className="member-center-profile__text-button"
              onClick={onModalOpen}
            >
              {t('users.membership.all_tiers')}
            </Button>
          </Box>
          <Modal
            isCentered
            size="4xl"
            isOpen={isModalOpen}
            onClose={onModalClose}
          >
            <ModalOverlay />
            <ModalContent className="member-detail__terms-modal">
              <ModalHeader>
                <Flex alignItems="flex-start">
                  <Box flex="1">
                    <Text
                      variant="modalSectionTitle"
                      className="member-center-profile__section-title-text"
                    >
                      {merchantData.name}
                      {t('users.membership.all_tiers.condition')}
                    </Text>
                  </Box>
                  <Button
                    variant="closeModal"
                    className="member-detail__terms-modal-close_button"
                    onClick={onModalClose}
                  >
                    <i className="fa fa-times" aria-hidden="true"></i>
                  </Button>
                </Flex>
                <Text
                  variant="hint"
                  className="member-center-profile__hint-text"
                >
                  {t('users.membership.all_tiers.description')}
                </Text>
              </ModalHeader>
              <ModalBody>
                <div>
                  {merchantTiers.map((tier, index) => (
                    <Box marginTop={index === 0 ? '' : '20px'} key={tier._id}>
                      <Text
                        variant="modalSectionTitle"
                        className="member-center-profile__section-title-text"
                      >
                        {translateModel(tier.name_translations, i18n.language)}
                      </Text>
                      {tier.member_discount_percentage && (
                        <Text
                          variant="modalDescription"
                          className="member-center-profile__description-text"
                          marginBottom="5px"
                        >
                          {t('users.membership.tier.shopping_discount', {
                            discount: transformPercentageToDiscount(
                              tier.member_discount_percentage,
                              i18n.language,
                            ),
                          })}
                        </Text>
                      )}
                      <Text
                        variant="hint"
                        className="member-center-profile__hint-text"
                      >
                        {t('users.membership.tier.upgrade.condition')}
                        {tierConditions[index].upgradeCondition}
                      </Text>
                      <Text
                        variant="hint"
                        className="member-center-profile__hint-text"
                      >
                        {t('users.membership.tier.expire.date')}
                        {tierConditions[index].tierExpireDate}
                      </Text>
                      {tierConditions[index].extendCondition && (
                        <Text
                          variant="hint"
                          className="member-center-profile__hint-text"
                        >
                          {t('users.membership.tier.extend.condition')}
                          {tierConditions[index].extendCondition}
                        </Text>
                      )}
                    </Box>
                  ))}
                </div>
              </ModalBody>
              <ModalFooter p="10px" />
            </ModalContent>
          </Modal>
        </>
      )}
    </Box>
  );
};

MemberDetailList.propTypes = {
  merchantTiers: PropTypes.arrayOf(merchantMembershipTierType),
  extendBalanceStatusWording: PropTypes.string,
  extendRule: memberTierRuleType,
  upgradeRule: memberTierRuleType,
};

export default MemberDetailList;
